/**
 * Things the console points at, shown by name.
 *
 * Before this, a screen that mentioned a member, a staff account or a
 * workflow job printed its uuid and left the operator to copy it and search
 * for it elsewhere. `EntityRef` renders the thing by its human label as a link
 * to its own admin page, with the id kept - small, muted, one click to copy -
 * for the rare case the raw value is what someone needs.
 *
 * Labels come from, in order:
 *   1. what the screen already has on hand (`label`), which costs nothing;
 *   2. one batched `POST /internal/references/resolve` for every ref the page
 *      rendered, when the generated client has that operation;
 *   3. the per-entity reads the client already has, deduped and cached;
 *   4. the kind's own noun ("Staff account") when nothing can name it.
 */

import * as sdk from '@closet-theory/api';
import {
  accountsApiV1InternalAccountsGet,
  getJobApiV1InternalOperatorWorkflowJobsJobsJobIdGet,
  getStylistApiV1InternalStylistsIdGet,
  getUserApiV1InternalUsersIdGet,
  getWardrobeBulkImportApiV1InternalWardrobeBulkImportsImportIdGet,
  listMembersApiV1InternalMembersGet,
  memberOutfitApiV1InternalUsersIdOutfitsOutfitIdGet,
} from '@closet-theory/api';
import { useCallback, useEffect, useId, useState, useSyncExternalStore } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router';
import { useClipboard } from '@ui/hooks/use-clipboard';
import { cx } from '@ui/utils/cx';
import { configureAdminApi, handleUnauthorized } from './auth';
import { RefThumbnail } from './garment-image';
import { workflowLabel } from './workflow-labels';

/* ------------------------------------------------------------------ kinds */

export const REF_KINDS = [
  'member',
  'staff',
  'stylist',
  'garment',
  'outfit',
  'wardrobe_import',
  'workflow_job',
  'look_request',
  'chat_thread',
] as const;

export type RefKind = (typeof REF_KINDS)[number];

function isRefKind(value: unknown): value is RefKind {
  return typeof value === 'string' && (REF_KINDS as readonly string[]).includes(value);
}

/** What one of each kind is called, in the words an operator uses. */
export const REF_NOUN: Record<RefKind, string> = {
  member: 'Member',
  staff: 'Staff account',
  stylist: 'Stylist',
  garment: 'Garment',
  outfit: 'Outfit',
  wardrobe_import: 'Wardrobe import',
  workflow_job: 'Workflow job',
  look_request: 'Look request',
  chat_thread: 'Chat thread',
};

const HAS_IMAGE: ReadonlySet<RefKind> = new Set(['member', 'garment', 'outfit']);

export type RefTarget = {
  kind: RefKind;
  id: string;
  /** The member a garment, outfit or chat belongs to - their pages hang off the member's. */
  memberId?: string | null | undefined;
};

/**
 * The admin page for a ref, or nothing when the console has no page for it.
 * Staff accounts, upload sessions and look requests have none, so they render
 * as plain labels rather than links that lead nowhere useful.
 */
export function refHref({ kind, id, memberId }: RefTarget): string | undefined {
  const member = memberId ? encodeURIComponent(memberId) : undefined;
  const own = encodeURIComponent(id);
  switch (kind) {
    case 'member':
      return `/consumers/${own}`;
    case 'stylist':
      return `/stylists/${own}`;
    case 'wardrobe_import':
      return `/wardrobe-imports/${own}`;
    case 'workflow_job':
      return `/workflow-jobs/${own}`;
    case 'garment':
      return member ? `/consumers/${member}?tab=wardrobe&garment=${own}` : undefined;
    case 'outfit':
      return member ? `/consumers/${member}?tab=outfits&outfit=${own}` : undefined;
    case 'chat_thread':
      return member ? `/consumers/${member}?tab=chat` : undefined;
    default:
      return undefined;
  }
}

/** The first block of a uuid: enough to tell two apart at a glance. */
export function shortId(id: string): string {
  const head = id.split('-')[0] ?? id;
  return head.length >= 6 && head.length < id.length ? head : id.length > 12 ? id.slice(0, 8) : id;
}

export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function looksLikeUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/* --------------------------------------------------------------- resolver */

export type ResolvedRef = {
  found: boolean;
  label?: string | undefined;
  secondary?: string | undefined;
  imageUrl?: string | undefined;
  /**
   * The kind the id really is, when the resolver found it as a different kind
   * from the one asked for: a member id under a staff key reads as the member.
   */
  resolvedKind?: RefKind | undefined;
};

type Entry =
  | { state: 'loading' }
  | { state: 'ready'; value: ResolvedRef }
  /** Nothing could name it: not missing, just unnamed. */
  | { state: 'unresolved' };

type Store = {
  entries: Map<string, Entry>;
  listeners: Map<string, Set<() => void>>;
  queue: Map<string, RefTarget>;
  scheduled: boolean;
  /** The batch endpoint answered 404/405/501 once; stop asking this session. */
  batchUnavailable: boolean;
  staff: Promise<Map<string, ResolvedRef>> | undefined;
};

function freshStore(): Store {
  return {
    entries: new Map(),
    listeners: new Map(),
    queue: new Map(),
    scheduled: false,
    batchUnavailable: false,
    staff: undefined,
  };
}

let store = freshStore();

/** Tests only: forget every resolved label. */
export function resetReferenceCache(): void {
  store = freshStore();
}

const refKey = (kind: RefKind, id: string) => `${kind}:${id}`;

function publish(key: string, entry: Entry): void {
  store.entries.set(key, entry);
  store.listeners.get(key)?.forEach((listener) => listener());
}

type BatchResolve = (options: { body: { refs: Array<{ kind: string; id: string }> } }) => Promise<{
  data?: { refs?: unknown } | undefined;
  error?: unknown;
  response?: { status?: number } | undefined;
}>;

/**
 * The batch operation, if this build's generated client has it. The backend
 * adds `POST /internal/references/resolve`; until the client is regenerated
 * against it there is nothing to find (the build warns the name is not
 * exported, which is expected) and every ref takes the fallback path.
 *
 * Read by its generated name straight off the namespace - never aliased or
 * scanned - so the bundler resolves it statically and still drops every
 * operation admin never calls. If the backend names its handler differently,
 * change the name here.
 */
function batchOperation(): BatchResolve | undefined {
  try {
    const found = (sdk as unknown as Record<string, unknown>)[
      'resolveReferencesApiV1InternalReferencesResolvePost'
    ];
    return typeof found === 'function' ? (found as BatchResolve) : undefined;
  } catch {
    // A strict module mock throws on reading an export it does not define.
    return undefined;
  }
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/** How long the batch may take before the page stops waiting and reads each ref itself. */
export const BATCH_TIMEOUT_MS = 5000;

/** The endpoint is simply not there: not deployed, or not this method. */
const BATCH_ABSENT: ReadonlySet<number> = new Set([404, 405, 501]);

/**
 * Ask the batch endpoint. Whatever goes wrong - it rejects, times out, or
 * answers with any error - every ref it did not name falls through to the
 * per-entity reads, so a resolver outage never blanks a reference. Only an
 * answer that says the endpoint does not exist stops the session asking;
 * anything else is treated as transient and the next page asks again.
 */
async function resolveBatch(targets: RefTarget[]): Promise<RefTarget[]> {
  const operation = batchOperation();
  if (!operation || store.batchUnavailable) return targets;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timedOut = new Promise<undefined>((settle) => {
      timer = setTimeout(() => settle(undefined), BATCH_TIMEOUT_MS);
    });
    const result = await Promise.race([
      operation({ body: { refs: targets.map(({ kind, id }) => ({ kind, id })) } }),
      timedOut,
    ]);
    if (!result) return targets;
    const status = result.response?.status;
    handleUnauthorized(status);
    if (status !== undefined && BATCH_ABSENT.has(status)) {
      store.batchUnavailable = true;
      return targets;
    }
    const rows = result.data?.refs;
    if (result.error || (status !== undefined && status >= 400) || !Array.isArray(rows)) {
      return targets;
    }
    const answered = new Set<string>();
    for (const row of rows as Array<Record<string, unknown>>) {
      const kind = row['kind'];
      const id = row['id'];
      if (typeof kind !== 'string' || typeof id !== 'string') continue;
      const key = refKey(kind as RefKind, id);
      answered.add(key);
      const resolvedKind = row['resolved_kind'];
      publish(key, {
        state: 'ready',
        value: {
          found: row['found'] !== false,
          label: text(row['label']),
          secondary: text(row['secondary']),
          imageUrl: text(row['image_url']),
          ...(isRefKind(resolvedKind) && resolvedKind !== kind ? { resolvedKind } : {}),
        },
      });
    }
    return targets.filter((target) => !answered.has(refKey(target.kind, target.id)));
  } catch {
    return targets;
  } finally {
    clearTimeout(timer);
  }
}

/** Every staff-shaped account, read once a session: there are few of them. */
function staffDirectory(): Promise<Map<string, ResolvedRef>> {
  store.staff ??= (async () => {
    const directory = new Map<string, ResolvedRef>();
    for (const role of ['super_admin', 'admin', 'manager', 'stylist'] as const) {
      try {
        const result = await accountsApiV1InternalAccountsGet({
          query: { limit: 100, offset: 0, role },
        });
        handleUnauthorized(result.response?.status);
        for (const account of result.data?.items ?? []) {
          directory.set(account.id, {
            found: true,
            label: account.name || account.email,
            secondary: account.name ? account.email : undefined,
          });
        }
      } catch {
        // One role's page failing leaves the others named.
      }
    }
    return directory;
  })();
  return store.staff;
}

/** One per-entity read, as the fallback for a single ref. */
async function resolveOne(target: RefTarget): Promise<Entry> {
  const { kind, id, memberId } = target;
  const settle = (status: number | undefined, value: ResolvedRef | undefined): Entry => {
    handleUnauthorized(status);
    if (status === 404) return { state: 'ready', value: { found: false } };
    return value ? { state: 'ready', value } : { state: 'unresolved' };
  };
  try {
    switch (kind) {
      case 'member': {
        const result = await getUserApiV1InternalUsersIdGet({ path: { id } });
        const member = result.data;
        return settle(
          result.response?.status,
          member && !result.error
            ? {
                found: true,
                label: member.name || member.email || undefined,
                secondary: member.name ? (member.email ?? undefined) : undefined,
              }
            : undefined,
        );
      }
      case 'stylist': {
        const result = await getStylistApiV1InternalStylistsIdGet({ path: { id } });
        const stylist = result.data;
        return settle(
          result.response?.status,
          stylist && !result.error
            ? { found: true, label: stylist.name || stylist.email, secondary: stylist.email }
            : undefined,
        );
      }
      case 'staff': {
        const found = (await staffDirectory()).get(id);
        return found ? { state: 'ready', value: found } : { state: 'unresolved' };
      }
      case 'workflow_job': {
        const result = await getJobApiV1InternalOperatorWorkflowJobsJobsJobIdGet({
          path: { job_id: id },
        });
        const job = result.data;
        return settle(
          result.response?.status,
          job && !result.error
            ? {
                found: true,
                label: workflowLabel(text(job['kind']) ?? text(job['workflow'])),
                secondary: text(job['status']),
              }
            : undefined,
        );
      }
      case 'wardrobe_import': {
        const result = await getWardrobeBulkImportApiV1InternalWardrobeBulkImportsImportIdGet({
          path: { import_id: id },
        });
        const status = result.data;
        return settle(
          result.response?.status,
          status && !result.error
            ? {
                found: true,
                label: `Wardrobe import of ${new Date(status.created_at).toLocaleDateString()}`,
                secondary: status.status,
              }
            : undefined,
        );
      }
      case 'outfit': {
        if (!memberId) return UNRESOLVED;
        const result = await memberOutfitApiV1InternalUsersIdOutfitsOutfitIdGet({
          path: { id: memberId, outfit_id: id },
        });
        const outfit = result.data;
        return settle(
          result.response?.status,
          outfit && !result.error
            ? {
                found: true,
                label: outfit.display_title,
                imageUrl: text(outfit['render_image_url']),
              }
            : undefined,
        );
      }
      default:
        // Garments, upload sessions, look requests and chat threads have no
        // read by id: the page's own label, or the noun, is all there is.
        return { state: 'unresolved' };
    }
  } catch {
    return { state: 'unresolved' };
  }
}

/** Kinds with some way to be named beyond what the page passed in. */
function canResolve(target: RefTarget): boolean {
  if (batchOperation() && !store.batchUnavailable) return true;
  switch (target.kind) {
    case 'member':
    case 'stylist':
    case 'staff':
    case 'workflow_job':
    case 'wardrobe_import':
      return true;
    case 'outfit':
      return Boolean(target.memberId);
    default:
      return false;
  }
}

const UNRESOLVED: Entry = { state: 'unresolved' };
const LOADING: Entry = { state: 'loading' };

const FALLBACK_CONCURRENCY = 6;

/** `refs` is `max_length=200` upstream: a bigger batch is refused whole. */
export const BATCH_MAX_REFS = 200;

async function flush(): Promise<void> {
  store.scheduled = false;
  const targets = [...store.queue.values()];
  store.queue.clear();
  if (!targets.length) return;
  configureAdminApi();
  const chunks: RefTarget[][] = [];
  for (let start = 0; start < targets.length; start += BATCH_MAX_REFS) {
    chunks.push(targets.slice(start, start + BATCH_MAX_REFS));
  }
  const remaining = (await Promise.all(chunks.map((chunk) => resolveBatch(chunk)))).flat();
  let next = 0;
  const worker = async () => {
    while (next < remaining.length) {
      const target = remaining[next++];
      if (!target) break;
      publish(refKey(target.kind, target.id), await resolveOne(target));
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(FALLBACK_CONCURRENCY, remaining.length) }, worker),
  );
}

/**
 * Ask for a ref's label. Every ref rendered in the same tick is collected and
 * sent together, so a table of twenty-five members is one request, not
 * twenty-five, once the batch endpoint exists.
 */
function request(target: RefTarget): void {
  const key = refKey(target.kind, target.id);
  if (store.entries.has(key)) return;
  if (!canResolve(target)) {
    store.entries.set(key, UNRESOLVED);
    return;
  }
  store.entries.set(key, LOADING);
  store.queue.set(key, target);
  if (!store.scheduled) {
    store.scheduled = true;
    setTimeout(() => void flush(), 0);
  }
}

/**
 * The resolved state of one ref. A `label` from the page wins outright and
 * never reaches the network.
 */
export function useReference(target: RefTarget, known?: string | null): Entry {
  const key = refKey(target.kind, target.id);
  const skip = Boolean(known) || !target.id;
  const subscribe = useCallback(
    (listener: () => void) => {
      if (skip) return () => undefined;
      let listeners = store.listeners.get(key);
      if (!listeners) {
        listeners = new Set();
        store.listeners.set(key, listeners);
      }
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    [key, skip],
  );
  const entry = useSyncExternalStore(subscribe, () =>
    skip ? UNRESOLVED : (store.entries.get(key) ?? (canResolve(target) ? LOADING : UNRESOLVED)),
  );
  useEffect(() => {
    if (!skip) request(target);
    // Keyed by kind and id; memberId only matters to the first request.
  }, [key, skip]);
  return entry;
}

/* -------------------------------------------------------------- rendering */

/**
 * A raw id, secondary: muted, shortened, and copied whole on click. For ids
 * that are genuinely the thing - a request id to quote to support - this is
 * also the whole rendering.
 */
export function CopyableId({
  value,
  label = 'id',
  full = false,
  className,
}: {
  value: string;
  /** What the id is, for the button's accessible name: "Copy member id". */
  label?: string;
  /** Show the whole value rather than its first block. */
  full?: boolean;
  className?: string;
}) {
  const { copied, copy } = useClipboard();
  const onClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    void copy(value);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${value} — click to copy`}
      aria-label={`Copy ${label} ${value}`}
      className={cx(
        'inline-flex max-w-full shrink-0 cursor-copy items-center gap-1 rounded px-1 font-mono text-[11px] text-quaternary outline-brand select-text hover:bg-secondary hover:text-tertiary focus-visible:outline-2',
        full && 'break-all',
        className,
      )}
    >
      <span className={full ? undefined : 'truncate'}>{full ? value : shortId(value)}</span>
      {copied ? <span className="font-sans text-brand-secondary">Copied</span> : null}
    </button>
  );
}

/**
 * A raw identifier with its human label above it and a copy button, for the
 * handful of ids an operator genuinely needs to quote. Same shape as the
 * console's `IdentifierValue`, plus the copy.
 */
export function IdentifierCopy({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex min-w-0 flex-col items-start gap-0.5">
      <span className="text-xs text-tertiary">{label}</span>
      <CopyableId value={value} label={label.toLowerCase()} full className="text-secondary" />
    </span>
  );
}

function initials(label: string): string {
  const parts = label
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean);
  return (parts[0]?.[0] ?? '?').toUpperCase() + (parts[1]?.[0] ?? '').toUpperCase();
}

function Thumb({ kind, label, src }: { kind: RefKind; label: string; src?: string | undefined }) {
  const [broken, setBroken] = useState(false);
  if (src && !broken)
    return <RefThumbnail src={src} round={kind === 'member'} onFailed={() => setBroken(true)} />;
  if (kind !== 'member') return null;
  return (
    <span
      aria-hidden="true"
      className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold text-tertiary"
    >
      {initials(label)}
    </span>
  );
}

function RefLink({
  href,
  children,
  className,
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  const routed = useInRouterContext();
  const stop = (event: MouseEvent) => event.stopPropagation();
  return routed ? (
    <Link to={href} onClick={stop} className={className}>
      {children}
    </Link>
  ) : (
    <a href={href} onClick={stop} className={className}>
      {children}
    </a>
  );
}

export type EntityRefProps = RefTarget & {
  /** The name the page already has; given, nothing is fetched. */
  label?: string | null | undefined;
  /** A second line of context: an email, a status. */
  secondary?: string | null | undefined;
  /** A thumbnail the page already has (signed garment/outfit image, avatar). */
  imageUrl?: string | null | undefined;
  /** Hide the trailing id; the copy is still in the link's title. */
  hideId?: boolean;
  className?: string;
};

/**
 * One entity by name, as a link to its admin page, with its id secondary.
 */
export function EntityRef({
  kind,
  id,
  memberId,
  label,
  secondary,
  imageUrl,
  hideId = false,
  className,
}: EntityRefProps) {
  const entry = useReference({ kind, id, memberId }, label);
  const describedBy = useId();
  if (!id) return <span className="text-quaternary">—</span>;
  const resolved = entry.state === 'ready' ? entry.value : undefined;
  // The resolver retries a person under the other kind, so a member id under
  // a staff key reads, and links, as the member it really is.
  const shownKind = resolved?.resolvedKind ?? kind;
  const noun = REF_NOUN[shownKind];
  // Not found says only that nothing of this kind has this id: it may have
  // been deleted, or never existed.
  const missing = resolved?.found === false;
  const name = label || resolved?.label || (missing ? `Unknown ${noun.toLowerCase()}` : noun);
  const detail = secondary || (missing ? undefined : resolved?.secondary);
  const image = imageUrl || resolved?.imageUrl;
  const href = missing ? undefined : refHref({ kind: shownKind, id, memberId });
  const loading = entry.state === 'loading' && !label;
  const primary = loading ? (
    <span
      aria-busy="true"
      aria-label={`Loading ${noun.toLowerCase()} name`}
      className="inline-block h-3 w-24 animate-pulse rounded bg-quaternary/40 align-middle"
    />
  ) : (
    <span
      className={cx(
        'truncate font-medium',
        missing ? 'text-tertiary italic' : 'text-primary',
        href && 'underline decoration-dotted underline-offset-2 hover:text-brand-secondary',
      )}
      {...(missing && !label
        ? { title: `No ${noun.toLowerCase()} has this id. It may have been deleted.` }
        : {})}
    >
      {name}
    </span>
  );
  return (
    <span
      className={cx('inline-flex max-w-full min-w-0 items-center gap-1.5 align-middle', className)}
      data-ref-kind={shownKind}
      data-ref-id={id}
    >
      {HAS_IMAGE.has(shownKind) && !loading ? (
        <Thumb kind={shownKind} label={name} src={image} />
      ) : null}
      <span className="flex min-w-0 flex-col">
        {href && !loading ? (
          <RefLink
            href={href}
            className="min-w-0 truncate text-sm outline-brand focus-visible:outline-2"
          >
            {primary}
          </RefLink>
        ) : (
          <span className="min-w-0 truncate text-sm">{primary}</span>
        )}
        {detail ? (
          <span id={describedBy} className="truncate text-xs font-normal text-tertiary">
            {detail}
          </span>
        ) : null}
      </span>
      {hideId ? null : <CopyableId value={id} label={`${noun.toLowerCase()} id`} />}
    </span>
  );
}

/* ------------------------------------------------ inferring refs from keys */

const MEMBER_KEYS = new Set([
  'member_id',
  'user_id',
  'consumer_id',
  'target_member_id',
  'member_user_id',
  'subject_member_id',
]);
const STAFF_KEYS = new Set([
  'created_by',
  'updated_by',
  'requested_by',
  'reviewed_by',
  'approved_by',
  'queued_by',
  'decided_by',
  'issued_by',
  'actor_id',
  'actor_user_id',
  'actor_account_id',
  'operator_id',
  'operator_user_id',
  'staff_id',
  'staff_user_id',
  'reviewer_id',
]);
const KIND_BY_KEY: Record<string, RefKind> = {
  stylist_id: 'stylist',
  assigned_stylist_id: 'stylist',
  garment_id: 'garment',
  paired_garment_id: 'garment',
  source_garment_id: 'garment',
  outfit_id: 'outfit',
  import_id: 'wardrobe_import',
  bulk_import_id: 'wardrobe_import',
  wardrobe_import_id: 'wardrobe_import',
  job_id: 'workflow_job',
  workflow_job_id: 'workflow_job',
  producing_job_id: 'workflow_job',
  source_job_id: 'workflow_job',
  parent_job_id: 'workflow_job',
  look_request_id: 'look_request',
  thread_id: 'chat_thread',
  chat_thread_id: 'chat_thread',
};

/** The kind a field names by its key, if it names one. */
export function refKindForKey(key: string): RefKind | undefined {
  const normalised = key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
  if (MEMBER_KEYS.has(normalised)) return 'member';
  if (STAFF_KEYS.has(normalised)) return 'staff';
  return KIND_BY_KEY[normalised];
}

/** The plain-language column heading for a ref-shaped key: "Member", "Queued by". */
export function refColumnLabel(key: string): string | undefined {
  const kind = refKindForKey(key);
  if (!kind) return undefined;
  if (kind === 'staff') {
    const words = key.replace(/_(id|user_id|account_id)$/, '').replace(/_/g, ' ');
    const phrase = /(^| )by$/.test(words) ? words : words === 'actor' ? 'Done by' : REF_NOUN.staff;
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
  }
  return REF_NOUN[kind];
}

function pickText(record: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = text(record[key]);
    if (value) return value;
  }
  return undefined;
}

/**
 * A ref for `record[key]`, with whatever label the record already carries
 * beside the id (`member_name` next to `member_id`, `created_by_name` next to
 * `created_by`), so a table that has the names never fetches them.
 */
export function refForField(
  key: string,
  value: unknown,
  record: Record<string, unknown> = {},
): EntityRefProps | undefined {
  const kind = refKindForKey(key);
  if (!kind || typeof value !== 'string' || !value.trim()) return undefined;
  // A staff-shaped key can carry an email or a role name instead of an id.
  if (kind === 'staff' && !looksLikeUuid(value)) return undefined;
  const stem = key.replace(/_id$/, '');
  const label = pickText(record, [
    `${stem}_name`,
    `${key}_name`,
    `${stem}_label`,
    `${stem}_title`,
    `${stem}_display_name`,
    `${stem}_email`,
    ...(kind === 'member' ? ['member_name', 'user_name', 'member_email', 'user_email'] : []),
  ]);
  const memberId = pickText(record, ['member_id', 'user_id', 'consumer_id']);
  const imageUrl =
    kind === 'garment' || kind === 'outfit'
      ? pickText(record, [`${stem}_image_url`, `${stem}_thumbnail_url`])
      : undefined;
  return {
    kind,
    id: value,
    ...(label ? { label } : {}),
    ...(memberId && kind !== 'member' ? { memberId } : {}),
    ...(imageUrl ? { imageUrl } : {}),
  };
}

/* ------------------------------------------------------------ member picker */

type MemberOption = { id: string; name: string; email?: string | null | undefined };

/** How many matches the picker asks for. */
export const MEMBER_MATCH_LIMIT = 8;

/**
 * Choose a member by name or email instead of pasting their uuid.
 *
 * Searches the member list the Consumers screen already reads. A pasted uuid
 * still works - it is taken as-is - so nothing an operator used to do stops
 * working.
 */
export function MemberPicker({
  label = 'Member',
  value,
  onChange,
  hint,
}: {
  label?: string;
  /** The chosen member's id, or '' for none. */
  value: string;
  onChange: (memberId: string) => void;
  hint?: string;
}) {
  const inputId = useId();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<MemberOption[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  /** The search the options on screen answer, so "no match" is said only after one. */
  const [answered, setAnswered] = useState('');

  useEffect(() => {
    const needle = query.trim();
    if (!open || needle.length < 2 || looksLikeUuid(needle)) {
      setOptions([]);
      setAnswered('');
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      setSearching(true);
      configureAdminApi();
      void listMembersApiV1InternalMembersGet({
        query: { query: needle, limit: MEMBER_MATCH_LIMIT, offset: 0 },
      })
        .then((result) => {
          handleUnauthorized(result.response?.status);
          if (!live) return;
          setOptions(result.data?.items ?? []);
          // A failed search is not "no member matches".
          setAnswered(result.error ? '' : needle);
        })
        .catch(() => {
          if (live) {
            setOptions([]);
            setAnswered('');
          }
        })
        .finally(() => {
          if (live) setSearching(false);
        });
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, open]);

  const noMatch = !searching && !options.length && answered !== '' && answered === query.trim();
  const capped = options.length >= MEMBER_MATCH_LIMIT;

  const choose = (id: string) => {
    onChange(id);
    setQuery('');
    setOpen(false);
  };

  if (value)
    return (
      <div className="flex min-w-56 flex-col gap-1.5">
        <span className="text-sm font-medium text-secondary">{label}</span>
        <span className="flex min-h-10 items-center gap-2 rounded-lg border border-primary bg-primary px-3 py-1.5">
          <EntityRef kind="member" id={value} />
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label={`Clear ${label.toLowerCase()}`}
            className="ml-auto text-xs text-tertiary underline outline-brand focus-visible:outline-2"
          >
            Clear
          </button>
        </span>
      </div>
    );

  return (
    <div className="relative flex min-w-56 flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-secondary">
        {label}
      </label>
      <input
        id={inputId}
        role="combobox"
        aria-expanded={open && (options.length > 0 || noMatch)}
        aria-controls={listId}
        aria-autocomplete="list"
        value={query}
        placeholder="Search by name or email"
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            const needle = query.trim();
            if (looksLikeUuid(needle)) choose(needle);
            else if (options[0]) choose(options[0].id);
          }
          if (event.key === 'Escape') setOpen(false);
        }}
        className="min-h-10 rounded-lg border border-primary bg-primary px-3 py-2 text-md text-primary shadow-xs outline-brand placeholder:text-placeholder focus-visible:outline-2"
      />
      {hint ? <span className="text-sm text-tertiary">{hint}</span> : null}
      {open && (options.length > 0 || searching || noMatch) ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={`${label} matches`}
          className="absolute top-full z-20 mt-1 flex max-h-72 w-full min-w-64 flex-col overflow-auto rounded-lg border border-secondary bg-primary p-1 shadow-lg"
        >
          {searching && !options.length ? (
            <li className="px-3 py-2 text-sm text-tertiary">Searching…</li>
          ) : null}
          {noMatch ? <li className="px-3 py-2 text-sm text-tertiary">No member matches</li> : null}
          {options.map((option) => (
            <li
              key={option.id}
              role="option"
              aria-selected={false}
              tabIndex={-1}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option.id);
              }}
              className="flex cursor-pointer flex-col rounded-md px-3 py-2 hover:bg-secondary"
            >
              <span className="text-sm font-medium text-primary">{option.name}</span>
              {option.email ? <span className="text-xs text-tertiary">{option.email}</span> : null}
            </li>
          ))}
          {capped ? (
            <li className="px-3 py-2 text-xs text-tertiary">
              Showing the first {MEMBER_MATCH_LIMIT} matches. Refine your search to find others.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
